import { useEffect, useState } from "react";
import { useContext } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { MultiSelect } from "primereact/multiselect";
import {
  BoxFull,
  Button,
  Dropdown,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { CustomInput, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useBank } from "../../hooks/useBank";
import {
  CURRENCY_FORMAT_OPTIONS,
  CURRENCY_OPTIONS,
  REVALIDATE_FORM_PROPS,
} from "../../constants";
import { InitialBankFormSchema } from "../../validations/schemas";
import { AppContext } from "../../context";
import { ErrorMessage } from "@hookform/error-message";
import { PrivilegeValidatorComponent } from "../../components/shared/PrivilegeValidatorComponent";

export const InitialBankConfigurationPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: {
      id: 0,
      name: "",
      segment: "",
      status: "",
      languages: [] as any,
      defaultLanguage: "",
      overrideDateFormat: false,
      groupSeparator: "",
      decimalSeparator: "",
      decimalNumber: "",
      showCurrencyCode: false,
      defaultCurrency: "",
    },
  });

  const {
    appInfo,
    languages,
    dateFormat,
    currencyInfo,
    currencyKeys: currencyKeysOrder,
    bankUpdate,
  } = useBank();
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const watchLanguages = useWatch({
    control,
    name: "languages",
  });

  const handleDefaultLanguage = (e: any) => {
    if (languages) {
      const newLanguagesInfo = languages.map((lan) => ({
        ...lan,
        isDefault: e.value === lan?.isoCode,
      }));
      setValue("languages", newLanguagesInfo);
      setValue(
        "defaultLanguage",
        newLanguagesInfo.find((lan) => lan.isDefault)?.isoCode || "",
        REVALIDATE_FORM_PROPS
      );
    }
  };

  const onCancel = () => {
    setAppState((prev) => ({
      ...prev,
      selectedConfig: null,
    }));
    navigate("/", { replace: true });
    window.scrollTo(0, 0);
  };

  const onSave = async () => {
    const formValues = getValues();
    const availableLanguages = Object.values(formValues.languages).map(
      (lan: any) => lan.isoCode
    );
    const actionStatus = await bankUpdate({
      application: {
        id: formValues?.id,
        name: formValues?.name,
        segment: formValues?.segment,
        status: formValues?.status,
        updatedby: user?.name,
      },
      languages: languages?.map((lan) => ({
        id: lan.id,
        isdefault: lan.isoCode === formValues.defaultLanguage,
        status: availableLanguages.includes(lan.isoCode)
          ? "ACTIVE"
          : "INACTIVE",
      })),
      globalization: {
        overrideDateFormat: formValues?.overrideDateFormat,
        groupSeparator: formValues?.groupSeparator,
        decimalNumber: formValues?.decimalNumber,
        decimalSeparator: formValues?.decimalSeparator,
        defaultCurrency: formValues?.defaultCurrency,
        showCurrencyCode: formValues?.showCurrencyCode,
      },
    });
    if (actionStatus === "SUCCESS") {
      reset(formValues);
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "cancel" | "save") => {
    switch (actionType) {
      case "save":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("InitialConfiguration.saveModalTitle"),
          content: t("InitialConfiguration.saveModalContent"),
          onConfirm: onSave,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("InitialConfiguration.cancelModalTitle"),
          content: t("InitialConfiguration.cancelModalContent"),
          onConfirm: onCancel,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (appInfo && languages && dateFormat && currencyInfo) {
      const newValues = {
        ...appInfo,
        ...dateFormat,
        ...currencyInfo,
        decimalNumber: `${currencyInfo.decimalNumber}`,
        languages: languages.filter((lan) => lan.status === "ACTIVE"),
        defaultLanguage: languages.find((lan) => lan.isDefault)?.isoCode,
      };
      reset(newValues);
    }
  }, [appInfo, languages, dateFormat, currencyInfo, reset]);

  useEffect(() => {
    if (watchLanguages?.length === 1) {
      setValue(
        "defaultLanguage",
        watchLanguages[0]?.isoCode || "",
        REVALIDATE_FORM_PROPS
      );
    }
  }, [watchLanguages, setValue]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("InitialConfiguration.initialConfigurationTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={"Configuración inicial"}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("InitialConfiguration.initialConfigurationTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />

        <div className="t-subheader-content">
          <span>
            {t("InitialConfiguration.initialConfigurationDescription")}
          </span>
          <span>
            {t("InitialConfiguration.initialConfiguration2Description")}
          </span>
        </div>
      </TransactionalSubheader>

      <BoxFull
        className="flex flex-column align-items-center justify-content-center mt-4 gap-4"
        variant="tertiary"
      >
        {appInfo && getValues() && (
          <div className="flex flex-column align-items-flex-start gap-4">
            <div className="inputlabel h4 pl-4">
              {t("InitialConfiguration.generalInformationTitle")}
            </div>
            <div className="form-container left py-5 px-8">
              <div className="form">
                {["name", "segment"].map((key, idx, arr) => (
                  <div key={`${key}-c`}>
                    <Controller
                      key={key as any}
                      name={key as any}
                      control={control}
                      render={({ field }) => {
                        return (
                          <CustomInput
                            key={key}
                            id="input-name"
                            className={`rd-only ${
                              idx !== arr.length - 1 ? "mb-3" : ""
                            }`}
                            label={t(`InitialConfiguration.${key}Label`)}
                            type={"text"}
                            autoComplete="off"
                            readOnly
                            {...field}
                          />
                        );
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {languages && getValues() && (
          <div className="flex flex-column align-items-flex-start gap-4">
            <div className="inputlabel h4 pl-4">
              {t("InitialConfiguration.LanguagesTitle")}
            </div>

            <div className="form-container left py-5 px-8">
              <div className="form">
                <Controller
                  key={"languages"}
                  name={"languages"}
                  control={control}
                  rules={InitialBankFormSchema(t).languages}
                  render={({ field: { value, ...field } }) => {
                    field.onChange = (e) => {
                      const newLanguages = languages.filter((lan) =>
                        e.value.includes(lan.isoCode)
                      );
                      if (newLanguages.length > 0) {
                        setValue(
                          "languages",
                          newLanguages,
                          REVALIDATE_FORM_PROPS
                        );
                      }
                    };
                    return (
                      <>
                        <label htmlFor="languages" className="block lb pb-2">
                          {t("InitialConfiguration.enabledLanguagesLabel")}
                        </label>
                        <MultiSelect
                          display="chip"
                          className="mb-4"
                          optionLabel="name"
                          optionValue="isoCode"
                          options={languages.map((lan) => ({
                            ...lan,
                            name: t(`Languages.${lan.isoCode}`),
                          }))}
                          value={getValues("languages").map(
                            (lan: any) => lan.isoCode
                          )}
                          {...field}
                        />
                      </>
                    );
                  }}
                />

                <Controller
                  key={"defaultLanguage"}
                  name={"defaultLanguage"}
                  control={control}
                  rules={InitialBankFormSchema(t).defaultLanguage}
                  render={({ field }) => {
                    field.onChange = handleDefaultLanguage;
                    return (
                      // @ts-ignore
                      <Dropdown
                        key="dfl"
                        className="dropdown"
                        label={t("InitialConfiguration.defaultLanguageLabel")}
                        placeholder=""
                        options={languages.map((lan) => ({
                          label: t(`Languages.${lan.isoCode}`),
                          value: lan.isoCode,
                        }))}
                        filter={false}
                        showClear={false}
                        {...field}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {dateFormat && getValues() && (
          <div className="flex flex-column align-items-flex-start gap-4">
            <div className="inputlabel h4 pl-4">
              {t("InitialConfiguration.dateFormatTitle")}
            </div>

            <div className="form-container left py-5 px-8">
              <div className="form">
                {Object.keys(dateFormat).map((key, idx, arr) => (
                  <div key={`${key}-c`}>
                    <Controller
                      key={key}
                      name={key as any}
                      control={control}
                      rules={(InitialBankFormSchema(t) as any)[key]}
                      render={({ field }) => {
                        const isBoolean =
                          typeof getValues(key as any) == "boolean";
                        const extraProps = {};
                        if (isBoolean) {
                          field.onChange = (e) => {
                            setValue(
                              key as any,
                              e.value,
                              REVALIDATE_FORM_PROPS
                            );
                          };
                        }
                        return (
                          <CustomInput
                            id={`input-label`}
                            label={t(`InitialConfiguration.${key}Label`)}
                            placeholder={t(
                              `InitialConfiguration.${key}Placeholder`
                            )}
                            type={isBoolean ? "boolean" : "text"}
                            autoComplete="off"
                            className={`${
                              idx !== arr.length - 1 ? "mb-4" : ""
                            }`}
                            {...field}
                            {...extraProps}
                          />
                        );
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {currencyInfo && getValues() && (
          <div className="flex flex-column align-items-flex-start gap-4">
            <div className="inputlabel h4 pl-4">
              {t("InitialConfiguration.currencyFormatTitle")}
            </div>

            <div className="form-container left py-5 px-8">
              <div className="form">
                {Object.keys(currencyInfo)
                  .sort(function (a, b) {
                    return (
                      currencyKeysOrder.indexOf(a) -
                      currencyKeysOrder.indexOf(b)
                    );
                  })
                  .map((key, idx, arr) => (
                    <div key={`${key}-c`}>
                      <Controller
                        key={key}
                        name={key as any}
                        control={control}
                        rules={(InitialBankFormSchema(t) as any)[key]}
                        render={({ field }) => {
                          const isBoolean =
                            typeof getValues(key as any) == "boolean";
                          const extraProps = {};
                          if (isBoolean) {
                            field.onChange = (e) => {
                              setValue(
                                key as any,
                                e.value,
                                REVALIDATE_FORM_PROPS
                              );
                            };
                          } else if (key === "decimalSeparator") {
                            field.onChange = (e) => {
                              if (e.value === getValues("groupSeparator")) {
                                setValue(
                                  "groupSeparator",
                                  getValues(key),
                                  REVALIDATE_FORM_PROPS
                                );
                              }
                              setValue(key, e.value, REVALIDATE_FORM_PROPS);
                            };
                          } else if (key === "groupSeparator") {
                            field.onChange = (e) => {
                              if (e.value === getValues("decimalSeparator")) {
                                setValue(
                                  "decimalSeparator",
                                  getValues(key),
                                  REVALIDATE_FORM_PROPS
                                );
                              }
                              setValue(key, e.value, REVALIDATE_FORM_PROPS);
                            };
                          } else if (key === "decimalNumber") {
                            (extraProps as any).keyfilter = /^[0-9]*$/;
                            (extraProps as any).maxLength = 1;
                          }
                          return [
                            "decimalSeparator",
                            "groupSeparator",
                            "defaultCurrency",
                          ].includes(key) ? (
                            // @ts-ignore
                            <Dropdown
                              className={`dropdown ${
                                idx !== arr.length - 1 ? "mb-4" : ""
                              }`}
                              label={t(`InitialConfiguration.${key}Label`)}
                              placeholder=""
                              options={
                                key === "defaultCurrency"
                                  ? CURRENCY_OPTIONS
                                  : CURRENCY_FORMAT_OPTIONS(t)
                              }
                              filter={key === "defaultCurrency"}
                              showClear={false}
                              hasTooltip
                              tooltipText=""
                              {...field}
                            />
                          ) : (
                            <CustomInput
                              id={`input-label`}
                              label={t(`InitialConfiguration.${key}Label`)}
                              placeholder={t(
                                `InitialConfiguration.${key}Placeholder`
                              )}
                              type={isBoolean ? "boolean" : "text"}
                              autoComplete="off"
                              className={`${
                                key === "decimalNumber" &&
                                errors.decimalNumber?.message
                                  ? "p-invalid"
                                  : idx !== arr.length - 1
                                  ? "mb-4"
                                  : ""
                              }`}
                              {...field}
                              {...extraProps}
                            />
                          );
                        }}
                      />
                      {key === "decimalNumber" && (
                        <ErrorMessage
                          key={`error-${key}`}
                          errors={errors}
                          name="decimalNumber"
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              className="p-error block"
                              style={{
                                marginTop: "0.5rem",
                                marginBottom: "1.5rem",
                              }}
                            >
                              {message}
                            </small>
                          )}
                        />
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {appInfo && languages && dateFormat && currencyInfo && (
          <div className="form-btngroup-footer my-5 p-0 gap-6">
            <Button
              variant="p-button-link"
              label={"Cancelar"}
              onClick={isDirty ? () => handleModalProps("cancel") : onCancel}
            />
            <PrivilegeValidatorComponent showButton={permissiondInfoByRole.UPDATE} >
              <Button
                variant="p-button"
                label={t("InitialConfiguration.saveButton")}
                onClick={() => handleModalProps("save")}
                disabled={!(isValid && isDirty)}
              />
            </PrivilegeValidatorComponent>
          </div>
        )}
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
